import React, { useState, useEffect } from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	TabPane,
	TabContent,
	Spinner,
	FormFeedback,
	FormGroup,
} from 'reactstrap';

// Formik
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';

//redux
import { setRequireCloseModal } from '../../../../slices/fuelTank/reducer';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import {
	addNewFuelTank,
	updateFuelTank,
} from '../../../../slices/fuelTank/thunk';
import {
	getEstablishmentList,
	getEstablishmentTypeList,
	getFeedingBargesList,
	getFuelTypeList,
} from '../../../../helpers/establishment/backend_helper';
import OSelect from '../../../../Components/OElements/OSelect';
import { getUnitMeasurement } from '../../../../slices/thunks';
import { getUnitMeasurementList } from '../../../../helpers/residue/backend_helper';
import OInput from '../../../../Components/OElements/OInput';

const FuelTankForm = ({
	dataFuelTank,
	isEdit,
	handleToggleModal,
	isOpen,
	activeTab,
	toggleTab,
}) => {
	const dispatch = useDispatch();

	const [selectFuelType, setSelectFuelType] = useState(null);
	const [selectSelectUnitMeasurement, setSelectUnitMeasurement] =
		useState(null);

	const [selectedEstablishmentType, setSelectedEstablishmentType] =
		useState(null);
	const [showFeedingBarges, setShowFeedingBarges] = useState(false);
	const [showEstablishment, setShowEstablishment] = useState(false);

	const selectorFuelTank = createSelector(
		state => state.FuelTank,
		result => ({
			isAdding: result.isAdding,
			isAdded: result.isAdded,
			isUpdating: result.isUpdating,
			isUpdated: result.isUpdated,
			isLoadingList: result.isLoadingList,
			requireCloseModal: result.requireCloseModal,
		})
	);
	const {
		isAdding,
		isAdded,
		isUpdating,
		isUpdated,
		isLoadingList,
		requireCloseModal,
	} = useSelector(selectorFuelTank);

	// validation
	const validation = useFormik({
		// enableReinitialize: use this flag when initial values needs to be changed
		enableReinitialize: true,
		initialValues: {
			name: (dataFuelTank && dataFuelTank.name) || '',
			capacity: (dataFuelTank && dataFuelTank.capacity) || '',
			stock: (dataFuelTank && dataFuelTank.stock) || '0',
			fuel_type_id: (dataFuelTank && dataFuelTank.fuel_type_id) || '',
			unit_measurement_id:
				(dataFuelTank && dataFuelTank.unit_measurement_id) || '',
			description: (dataFuelTank && dataFuelTank.description) || '',

			// Identificación y estado de generación
			feeding_barges_id: dataFuelTank?.feeding_barges_id || null,
			establishment_id: dataFuelTank?.establishment_id || null,
			establishment_type_id: dataFuelTank?.establishment_type_id || null,
		},
		validationSchema: Yup.object({
			name: Yup.string().required('Campo requerido'),
			capacity: Yup.number()
				.required('Campo requerido')
				.min(0, 'La capacidad no puede ser negativa'),
			stock: Yup.number()
				.min(0, 'El stock no puede ser negativo')
				.test(
					'stock-no-supera-capacity',
					'El stock no puede superar la capacidad',
					function (value) {
						const { capacity } = this.parent; // Accede a otros valores del formulario
						return value <= capacity; // Valida que el stock no supere la capacidad
					}
				),
			fuel_type_id: Yup.string().required('Campo requerido'),
			unit_measurement_id: Yup.string().required('Campo requerido'),
			description: Yup.string().nullable(),

			// Identificación y estado de generación
			establishment_type_id: Yup.string().required('Campo requerido'),
			feeding_barges_id: Yup.lazy(() =>
				selectedEstablishmentType?.barges_needed
					? Yup.string().required('Campo requerido')
					: Yup.string().nullable()
			),
			establishment_id: Yup.lazy(() =>
				selectedEstablishmentType &&
				!selectedEstablishmentType?.barges_needed
					? Yup.string().required('Campo requerido')
					: Yup.string().nullable()
			),
		}),
		onSubmit: values => {
			const fuelTank = { ...values };

			if (selectedEstablishmentType?.barges_needed) {
				fuelTank['establishment_id'] = null;
			} else {
				fuelTank['feeding_barges_id'] = null;
			}

			if (isEdit) {
				fuelTank._id = dataFuelTank._id;
				dispatch(updateFuelTank(fuelTank));
			} else {
				dispatch(addNewFuelTank(fuelTank));
			}
		},
	});

	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal]);

	const handleEstablishmentTypeSelect = optionSelected => {
		setSelectedEstablishmentType(optionSelected);
		if (optionSelected) {
			if (optionSelected?.barges_needed) {
				setShowFeedingBarges(true);
				setShowEstablishment(false);
			} else {
				setShowFeedingBarges(false);
				setShowEstablishment(true);
			}
		} else {
			setShowFeedingBarges(false);
			setShowEstablishment(false);
		}
	};

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) {
					toast.error(
						'Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.',
						{ autoClose: 4000 }
					);
				}
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<Row className='g-3'>
					<Col lg={3}>
						<OInput
							name='name'
							label='Nombre'
							type='text'
							validation={validation}
							isRequired={true}
							classNameLabel='form-label'
							classNameInput='form-control'
						/>
					</Col>
					<Col lg={3}>
						<OInput
							name='capacity'
							label='Capacidad'
							type='text'
							validation={validation}
							isRequired={true}
							classNameLabel='form-label'
							classNameInput='form-control'
						/>
					</Col>
					<Col lg={3}>
						<OInput
							disabled
							name='stock'
							label='Saldo'
							type='text'
							validation={validation}
							classNameLabel='form-label'
							classNameInput='form-control'
						/>
					</Col>
					<Col lg={3}>
						<OSelect
							key={'fuel_type_id'}
							label='Tipo de Combustible'
							id='fuel_type_id'
							name='fuel_type_id'
							isRequired={true}
							value={validation.values.fuel_type_id || ''}
							onSelect={setSelectFuelType}
							requestApi={() => getFuelTypeList({})}
							validation={validation}
						/>
					</Col>
					<Col lg={3}>
						<OSelect
							key={'unit_measurement_id'}
							label='Unidad de Medida'
							id='unit_measurement_id'
							name='unit_measurement_id'
							isRequired={true}
							value={validation.values.unit_measurement_id || ''}
							onSelect={setSelectUnitMeasurement}
							requestApi={() => getUnitMeasurementList({})}
							validation={validation}
						/>
					</Col>

					<Col lg={3}>
						<OSelect
							label='Tipo de Establecimiento'
							id='establishment_type_id'
							name='establishment_type_id'
							isRequired={true}
							value={validation.values.establishment_type_id || ''}
							onSelect={handleEstablishmentTypeSelect}
							requestApi={() => getEstablishmentTypeList({})}
							validation={validation}
						/>
					</Col>
					{showEstablishment && (
						<Col lg={3}>
							<OSelect
								key={
									'establishment_id' +
									(validation.values.establishment_type_id || '')
								}
								label='Establecimiento'
								id='establishment_id'
								name='establishment_id'
								isRequired={true}
								value={dataFuelTank?.establishment?._id || ''}
								onChange={validation.handleChange}
								requestApi={() =>
									getEstablishmentList({
										establishment_type_id:
											selectedEstablishmentType?._id || null,
									})
								}
								validation={validation}
							/>
						</Col>
					)}

					{showFeedingBarges && (
						<Col lg={3}>
							<OSelect
								label='Artefacto Naval'
								id='feeding_barges_id'
								name='feeding_barges_id'
								isRequired={true}
								value={validation.values.feeding_barges_id || ''}
								onChange={validation.handleChange}
								requestApi={() => getFeedingBargesList({})}
								validation={validation}
							/>
						</Col>
					)}

					<Col lg={12}>
						<Label for='description' className='form-label'>
							Descripción
						</Label>
						<Input
							name='description'
							id='description'
							className='form-control'
							placeholder='Ingrese una descripción'
							type='textarea'
							onChange={validation.handleChange}
							onBlur={validation.handleBlur}
							value={validation.values.description || ''}
							invalid={
								validation.touched.description &&
								validation.errors.description
									? true
									: false
							}
						/>
						{validation.touched.description &&
							validation.errors.description && (
								<FormFeedback type='invalid'>
									{validation.errors.description}
								</FormFeedback>
							)}
					</Col>
				</Row>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 modal-footer-add-btn'>
					<Button
						type='button'
						onClick={() => {
							handleToggleModal();
						}}
						className='btn-danger btn-label'
					>
						<i className='icon-x label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={isAdding || isUpdating}
					>
						{isAdding || isUpdating ? (
							<i className='label-icon'>
								<Spinner size='sm' />
							</i>
						) : (
							<i className='ri-save-3-line label-icon'></i>
						)}
						{isEdit ? 'Editar' : 'Crear'} Estanque de Combustible
					</button>
				</div>
			</div>
		</Form>
	);
};

export default FuelTankForm;
